import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

export default function RegisterStudentPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [classes, setClasses] = useState([]); // store fetched classes
  const [loadingClasses, setLoadingClasses] = useState(true);

  const [formData, setFormData] = useState({
    student_name: "",
    age: "",
    student_class: "",
    parent_guardian_name: "",
    parent_email: "",
    parent_phone: "",
    notes: "",
  });

  // Fetch classes from backend
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await fetch(
          "https://doshfebackend.teemah.com.ng/api/assessments/classes/"
        );
        if (response.ok) {
          const data = await response.json();
          setClasses(data); // Assuming API returns a list of classes
        } else {
          console.error("Failed to fetch classes");
        }
      } catch (error) {
        console.error("Error fetching classes:", error);
      } finally {
        setLoadingClasses(false);
      }
    };

    fetchClasses();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(
        "https://doshfebackend.teemah.com.ng/api/assessments/students/",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        const student = await response.json();  // ✅ parse backend response
        Swal.fire({
    title: "Registration Successful!",
    html: `
      <div style="text-align:left; font-size: 14px;">
        <p><strong>Instructions:</strong></p>
        <ul style="list-style:disc; margin-left:18px;">
          <li>Please, let your pupil do this independently.</li>
          <li>Do not assist with reading or explanation, as you won’t know the true ability of the child.</li>
          <li>No cheating please.</li>
          <li>If you need working material (pen, pencil, rough sheet), get it ready before you begin.</li>
          <li>The test is slated for <strong>20 minutes</strong>.</li>
          <li>Read each question carefully and select the best answer.</li>
          <li>Once submitted, you cannot retake the test.</li>
          <li>Best of luck! 🍀</li>
        </ul>
      </div>
    `,
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6", // ✅ blue button
        }).then((result) => {
          if (result.isConfirmed) {
        navigate(`/questions/${student.id}`);
 // redirect after clicking OK
      } // redirect after success
        });

        setFormData({
          student_name: "",
          age: "",
          student_class: "",
          class_student:"",
          parent_guardian_name: "",
          parent_email: "",
          parent_phone: "",
          notes: "",
        });
      } else {
        Swal.fire({
          title: "Error",
          text: "Something went wrong while registering.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: "Network Error",
        text: "Please check your internet connection.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }

    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-2xl bg-white text-gray-900 rounded-2xl shadow-2xl border p-6 md:p-8 mt-20">
        <h2 className="text-2xl md:text-3xl font-bold text-indigo-900">
          Free Maths Assessment Signup
        </h2>
        <p className="text-gray-600 mt-1">
          Tell us a few details and we’ll guide you to the right diagnostic
          test.
        </p>

        <form
          onSubmit={handleSubmit}
          className="grid md:grid-cols-2 gap-4 mt-6"
        >
          {/* Child Name */}
          <div>
            <label className="block text-sm text-gray-700">
              Child’s Full Name
            </label>
            <input
              name="student_name"
              value={formData.student_name}
              onChange={handleChange}
              required
              className="mt-1 w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Age */}
          <div>
            <label className="block text-sm text-gray-700">Age</label>
            <input
              name="age"
              type="number"
              min="3"
              max="18"
              value={formData.age}
              onChange={handleChange}
              required
              className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Current Class */}
          <div>
            <label className="block text-sm text-gray-700">Current Class</label>
            <select
              name="student_class"
              value={formData.student_class}
              onChange={handleChange}
              required
              disabled={loadingClasses}
              className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">
                {loadingClasses ? "Loading classes..." : "Select"}
              </option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>
          </div>

          {/* Parent Name */}
          <div>
            <label className="block text-sm text-gray-700">
              Parent/Guardian Name
            </label>
            <input
              name="parent_guardian_name"
              value={formData.parent_guardian_name}
              onChange={handleChange}
              required
              className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Parent Email */}
          <div>
            <label className="block text-sm text-gray-700">Parent Email</label>
            <input
              name="parent_email"
              type="email"
              value={formData.parent_email}
              onChange={handleChange}
              required
              className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Parent Phone */}
          <div>
            <label className="block text-sm text-gray-700">Parent Phone</label>
            <input
              name="parent_phone"
              type="tel"
              value={formData.parent_phone}
              onChange={handleChange}
              required
              className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Notes */}
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-700">
              Notes (optional)
            </label>
            <textarea
              name="notes"
              rows="3"
              value={formData.notes}
              onChange={handleChange}
              className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="Anything we should know about your child’s learning?"
            />
          </div>

          {/* Submit Button */}
          <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mt-2">
            <p className="text-xs text-gray-600">
              100% confidential • We’ll email strengths, weaknesses & next
              steps.
            </p>
            <button
              disabled={submitting}
              className="px-6 py-3 bg-indigo-700 text-white font-semibold rounded-lg hover:bg-indigo-600 transition disabled:opacity-60"
            >
              {submitting ? "Submitting..." : "Begin Assessment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
